// Modelo puro do delegation log (Parte 5.2/5.5/5.7/14; REQ-AUTHZ-006..008/010/022; GIY-075): situações e variante do selo, motivos de situação, esquema
// do formulário de delegação (zod), a versão corrente de cada pessoa, os estudos que `/me` entrega e as permissões do estudo. Sem React nem rede:
// testável por unidade e fora dos arquivos de componente (Fast Refresh).
import { type BlockingConstraint, GiyusApiError, type MeContext } from '@giyus/api-client';
import { z } from 'zod';

import { tenantRolePermissions } from '../../../lib/permissions/rolePermissions';
import { UUID } from '../components/forms';
import type { BadgeVariant } from '../components/statusVariant';
import type {
  Delegation,
  DelegationBlockReason,
  DelegationTaskType,
  SaveDelegationInput,
} from '../api/delegations';

/** As oito situações da máquina de estados (Parte 5.5) mais a derivada `Scheduled` (ativa, mas a vigência ainda não começou). */
export const DELEGATION_STATUSES = [
  'Draft',
  'PendingAcceptance',
  'Active',
  'SuspendedCertification',
  'SuspendedLink',
  'Revoked',
  'Expired',
  'Superseded',
] as const;
export type DelegationStatusCode = (typeof DELEGATION_STATUSES)[number];

const TERMINAL: ReadonlySet<string> = new Set(['Revoked', 'Expired', 'Superseded']);
const PENDING: ReadonlySet<string> = new Set(['Draft', 'PendingAcceptance']);
const CURRENT: ReadonlySet<string> = new Set(['Active', 'SuspendedCertification', 'SuspendedLink']);

export function isTerminal(status: string): boolean {
  return TERMINAL.has(status);
}

export function isPending(status: string): boolean {
  return PENDING.has(status);
}

/** Em curso: ativa ou suspensa (a pessoa a detém, ainda que sem acesso). */
export function isCurrent(status: string): boolean {
  return CURRENT.has(status);
}

/** A situação que a tela mostra: `Scheduled` (derivada pelo servidor) tem precedência sobre `Active`. */
export function displayStatus(delegation: Pick<Delegation, 'status' | 'effectiveStatus'>): string {
  return delegation.effectiveStatus === 'Scheduled' ? 'Scheduled' : delegation.status;
}

/** Variante do selo por situação: ativa em destaque, a caminho em neutro, suspensa/revogada em alerta, encerrada em contorno. */
export function delegationStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'Active':
      return 'default';
    case 'Draft':
    case 'PendingAcceptance':
    case 'Scheduled':
      return 'secondary';
    case 'SuspendedCertification':
    case 'SuspendedLink':
    case 'Revoked':
      return 'destructive';
    default:
      return 'outline';
  }
}

/** Códigos de razão que o sistema grava em `statusReason` (Parte 5.5/5.8); a razão da revogação pelo PI é texto livre. */
export const STATUS_REASON_CODES = [
  'CERTIFICATION_EXPIRED',
  'CERTIFICATION_REVOKED',
  'LICENSE_EXPIRED',
  'ABSENCE_WITHDRAWAL',
  'LINK_ENDED',
  'PERSON_OFFBOARDED',
  'TENANT_OFFBOARDED',
  'USER_DISABLED_OFFBOARDED',
  'USER_DISABLED_EXPIRED',
  'ACCESS_REVIEW_REVOKED',
  'PERIOD_ENDED',
  'SUPERSEDED',
] as const;
const REASON_CODES: ReadonlySet<string> = new Set(STATUS_REASON_CODES);

/** Chave i18n (`authz:delegations.statusReasons.*`) do motivo da situação; `null` quando é texto livre (razão da revogação) ou não há motivo. */
export function statusReasonKey(reason: string | null): string | null {
  return reason !== null && REASON_CODES.has(reason) ? `delegations.statusReasons.${reason}` : null;
}

/** Códigos de trava do contrato (`blockingConstraints[].code`, Parte 5.2 a–d) com tradução própria em `authz:delegations.blocked.reasons.*`. */
export const BLOCK_REASON_CODES = [
  'LINK_NOT_ACTIVE',
  'CERTIFICATION_MISSING',
  'LICENSE_MISSING',
  'FUNCTION_NOT_ALLOWED',
  'SKILL_MISSING',
  'PEOPLE_DATA_UNAVAILABLE',
] as const;
const BLOCK_CODES: ReadonlySet<string> = new Set(BLOCK_REASON_CODES);

export function isKnownBlockReason(code: string): boolean {
  return BLOCK_CODES.has(code);
}

/** O motivo de uma trava em formato único, venha do `blockingConstraints[]` do 423 ou de uma tentativa registrada. */
export function toBlockReason(
  source: BlockingConstraint | DelegationBlockReason,
): DelegationBlockReason {
  return {
    code: source.code,
    detail: source.detail ?? null,
    certificationTypeCode: source.certificationTypeCode ?? null,
    taskCode: source.taskCode ?? null,
    councilCode: source.councilCode ?? null,
  };
}

const MISSING_SKILL_DETAIL = /^Missing skill (.+) for task \S+$/;

/**
 * As capacidades (`Skill`) que faltam num motivo `SKILL_MISSING`. O contrato não tem campo próprio: elas vêm no `detail` técnico, estável e em inglês
 * (`Missing skill ECG_12_LEAD for task ECG`, vários separados por vírgula). Sem o padrão esperado, a lista é vazia e a mensagem fica genérica.
 */
export function skillCodesOf(reason: Pick<DelegationBlockReason, 'detail'>): string[] {
  const match = MISSING_SKILL_DETAIL.exec(reason.detail ?? '');
  return match?.[1]
    ? match[1]
        .split(',')
        .map((code) => code.trim())
        .filter((code) => code !== '')
    : [];
}

/** Motivos de um 423 `AUTHZ_DELEGATION_BLOCKED` (a ordem do servidor é a determinística: vínculo, certificações, licenças, função, skill). */
export function reasonsFromConstraints(
  constraints: readonly BlockingConstraint[],
): DelegationBlockReason[] {
  return constraints.map(toBlockReason);
}

/** Os motivos de um 423 `AUTHZ_DELEGATION_BLOCKED` (rascunho, emenda, assinatura do PI ou aceite); `null` para qualquer outro erro. */
export function blockedReasonsOf(error: unknown): DelegationBlockReason[] | null {
  return error instanceof GiyusApiError && error.errorCode === 'AUTHZ_DELEGATION_BLOCKED'
    ? reasonsFromConstraints(error.blockingConstraints)
    : null;
}

// ---- Formulário (criação e emenda) ----

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Hoje em UTC (`yyyy-MM-dd`): a data com que o servidor confere "início da vigência de hoje em diante" (a mesma base de `RoleAssignment`). */
export function utcToday(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** Mensagens são chaves de `authz:delegations.form.errors.*`, traduzidas pelo formulário. */
export const delegationFormSchema = z
  .object({
    personId: z.string().regex(UUID, 'delegations.form.errors.personId'),
    functionCode: z.string().trim().min(1, 'delegations.form.errors.function'),
    tasks: z.array(z.string()).min(1, 'delegations.form.errors.tasks'),
    effectiveFrom: z.string().regex(ISO_DATE, 'delegations.form.errors.from'),
    effectiveTo: z
      .string()
      .refine((value) => value === '' || ISO_DATE.test(value), 'delegations.form.errors.to'),
  })
  .refine((value) => value.effectiveTo === '' || value.effectiveFrom <= value.effectiveTo, {
    message: 'delegations.form.errors.order',
    path: ['effectiveTo'],
  });

export type DelegationFormValues = z.infer<typeof delegationFormSchema>;

/** Valores do formulário → corpo de `POST …/delegations`. */
export function toCreateInput(values: DelegationFormValues): SaveDelegationInput {
  return {
    personId: values.personId,
    functionCode: values.functionCode.trim(),
    tasks: [...values.tasks],
    effectiveFrom: values.effectiveFrom,
    effectiveTo: values.effectiveTo === '' ? null : values.effectiveTo,
  };
}

/**
 * Valores iniciais de uma emenda: a delegação emendada, tarefa a tarefa. A emenda começa HOJE (o aceite supersede a versão anterior de imediato; um início futuro deixaria
 * a pessoa sem acesso até a data e o servidor responde 422 `effectiveFrom: in_future`) — nem a vigência agendada da origem é proposta como início.
 */
export function amendmentDefaults(delegation: Delegation, today: string): DelegationFormValues {
  return {
    personId: delegation.personId,
    functionCode: delegation.functionCode,
    tasks: delegation.tasks.map((item) => item.taskCode),
    effectiveFrom: today,
    effectiveTo: delegation.effectiveTo ?? '',
  };
}

/** Tarefas do catálogo que a função admite (a trava `FUNCTION_NOT_ALLOWED` do servidor é a autoridade; aqui só ordena a escolha). */
export function tasksAllowedFor(
  catalog: readonly DelegationTaskType[],
  functionCode: string,
): {
  readonly allowed: readonly DelegationTaskType[];
  readonly others: readonly DelegationTaskType[];
} {
  const active = catalog.filter((item) => item.status === 'Active');
  const allowed = active.filter((item) => item.allowedFunctionCodes.includes(functionCode));
  const others = active.filter((item) => !item.allowedFunctionCodes.includes(functionCode));
  return { allowed, others };
}

/** Funções que o catálogo admite em alguma tarefa (E.3), ordenadas — alimenta o seletor de função. */
export function functionsOf(catalog: readonly DelegationTaskType[]): string[] {
  return [...new Set(catalog.flatMap((item) => item.allowedFunctionCodes))].sort();
}

// ---- Versões ----

/**
 * A versão corrente de cada pessoa: a delegação em curso (ativa/suspensa) ou, sem ela, a pendente (rascunho/aguardando aceite) — a que
 * o log mostra como "vigente". Quando há as duas (emenda em andamento), a em curso vale até o aceite da nova. Terminais ficam no histórico.
 */
export function currentVersionOf(delegations: readonly Delegation[]): Delegation[] {
  const byPerson = new Map<string, Delegation[]>();
  for (const delegation of delegations) {
    byPerson.set(delegation.personId, [...(byPerson.get(delegation.personId) ?? []), delegation]);
  }
  const result: Delegation[] = [];
  for (const group of byPerson.values()) {
    const live = group.filter((item) => !isTerminal(item.status));
    result.push(...live.sort((a, b) => Number(a.version) - Number(b.version)));
  }
  return result.sort((a, b) =>
    (a.personDisplayName ?? a.personId).localeCompare(b.personDisplayName ?? b.personId),
  );
}

/** O histórico completo, da versão mais nova à mais antiga (a API já o devolve assim; a tela não depende disso). */
export function historyOf(delegations: readonly Delegation[]): Delegation[] {
  return [...delegations].sort(
    (a, b) => b.createdAt.localeCompare(a.createdAt) || Number(b.version) - Number(a.version),
  );
}

// ---- Ações possíveis (a API é a autoridade; aqui só se esconde o que não cabe) ----

export function canSignDelegation(delegation: Pick<Delegation, 'status'>): boolean {
  return delegation.status === 'Draft';
}

export function canAmendDelegation(
  delegation: Pick<Delegation, 'status' | 'personId'>,
  all: readonly Pick<Delegation, 'status' | 'personId'>[],
): boolean {
  return (
    delegation.status === 'Active' &&
    !all.some((item) => item.personId === delegation.personId && isPending(item.status))
  );
}

export function canRevokeDelegation(delegation: Pick<Delegation, 'status'>): boolean {
  return !isTerminal(delegation.status);
}

// ---- Estudos de `/me` e permissões do estudo ----

export interface StudyEntry {
  readonly studySiteId: string;
  readonly roles: readonly string[];
}

/** Estudos do contexto: os papéis de escopo `Study` de `/me` (delegação, PI, revisor), agrupados por estudo. */
export function studiesOf(context: MeContext | undefined): StudyEntry[] {
  const byStudy = new Map<string, Set<string>>();
  for (const effective of context?.effectiveRoles ?? []) {
    if (effective.scopeType === 'Study') {
      byStudy.set(
        effective.scopeId,
        (byStudy.get(effective.scopeId) ?? new Set()).add(effective.role),
      );
    }
  }
  return [...byStudy.entries()]
    .map(([studySiteId, roles]) => ({ studySiteId, roles: [...roles].sort() }))
    .sort((a, b) => a.studySiteId.localeCompare(b.studySiteId));
}

/** O estudo está entre os que `/me` entrega? Fora → NotFound local, sem chamar a API (REQ-FE-004). */
export function hasStudy(context: MeContext | undefined, studySiteId: string): boolean {
  return studiesOf(context).some((item) => item.studySiteId === studySiteId);
}

export interface StudyPermissions {
  readonly canRead: boolean;
  readonly canManage: boolean;
  readonly canSign: boolean;
}

/** Permissões no estudo: papéis de escopo `Study` deste estudo e de tenant/site (o servidor decide; aqui só se escondem os botões). */
export function studyPermissions(
  context: MeContext | undefined,
  studySiteId: string,
): StudyPermissions {
  const held = new Set<string>();
  for (const effective of context?.effectiveRoles ?? []) {
    const applies =
      effective.scopeType === 'Study'
        ? effective.scopeId === studySiteId
        : effective.scopeType === 'Tenant' || effective.scopeType === 'Site';
    if (applies) {
      for (const permission of tenantRolePermissions(effective.role)) {
        held.add(permission);
      }
    }
  }
  return {
    canRead: held.has('delegation.read'),
    canManage: held.has('delegation.manage'),
    canSign: held.has('delegation.sign'),
  };
}

/** Rótulo curto do estudo até o cadastro de estudos existir (M10): os 8 últimos caracteres do id. TODO(GIY-118): trocar pelo protocolo e título. */
export function shortId(id: string): string {
  return id.slice(-8);
}

/** Hash curto para exibição (o completo vai no `title`/diálogo de assinatura). */
export function shortHash(hash: string): string {
  return hash.slice(0, 12);
}
